import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { toast } from 'react-toastify';

import { notificationApi } from '../api/endpoints.js';
import type { Notification } from '../api/endpoints.js';
import { useSession } from '../auth/session.js';
import { queryKeys } from './query-client.js';

interface NotificationsValue {
  readonly items: readonly Notification[];
  readonly unread: number;
  readonly markRead: (id: string) => Promise<void>;
  readonly markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsValue | null>(null);

export function NotificationsProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [items, setItems] = useState<readonly Notification[]>([]);
  const [unread, setUnread] = useState(0);

  const goTo = useRef(navigate);

  useEffect(() => {
    goTo.current = navigate;
  }, [navigate]);

  const userId = user?.id ?? null;

  const [lastUserId, setLastUserId] = useState(userId);

  if (lastUserId !== userId) {
    setLastUserId(userId);
    setItems([]);
    setUnread(0);
  }

  useEffect(() => {
    if (userId === null) return;

    let live = true;

    const reconcile = (): void => {
      void notificationApi.list().then((page) => {
        if (!live) return;
        setItems(page.items);
        setUnread(page.unread);
      });
    };

    reconcile();

    const socket: Socket = io({ withCredentials: true, transports: ['websocket'] });

    socket.on('notification', (payload: Notification) => {
      setItems((current) => [payload, ...current].slice(0, 50));
      setUnread((current) => current + 1);

      toast.info(payload.title, {
        onClick: () => {
          void goTo.current(`/tickets/${payload.ticketId}`);
        },
      });

      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });

      reconcile();
    });

    socket.on('connect', reconcile);

    return () => {
      live = false;
      socket.close();
    };
  }, [userId, queryClient]);

  const markRead = useCallback(async (id: string): Promise<void> => {
    setItems((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, readAt: new Date().toISOString() } : entry)),
    );
    setUnread((current) => Math.max(0, current - 1));
    await notificationApi.markRead(id);
  }, []);

  const markAllRead = useCallback(async (): Promise<void> => {
    const now = new Date().toISOString();
    setItems((current) => current.map((entry) => ({ ...entry, readAt: entry.readAt ?? now })));
    setUnread(0);
    await notificationApi.markAllRead();
  }, []);

  const value = useMemo<NotificationsValue>(
    () => ({ items, unread, markRead, markAllRead }),
    [items, unread, markRead, markAllRead],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsValue {
  const value = useContext(NotificationsContext);

  if (value === null) {
    throw new Error('useNotifications must be used inside NotificationsProvider.');
  }

  return value;
}
